import {Injectable} from '@angular/core';
import {firstValueFrom, Observable} from 'rxjs';
import {HttpClient} from '@angular/common/http';

import GeoJSON from 'ol/format/GeoJSON.js';
import {searchFlurUrl, titleCounty, titleDistrict, titleKommunen, titleState} from "../var/constants";
import {GeoJSONFeatureCollection} from "ol/format/GeoJSON";

@Injectable({
  providedIn: 'root'
})
export class RegionsService {

  regions: {
    bundesland: string[];
    bezirke: string[];
    kreise: string[];
    kommunen: string[]
  } = {
    bundesland: [],
    bezirke: [],
    kreise: [],
    kommunen: []
  }

  bundeslandGeom: GeoJSONFeatureCollection | undefined;
  kommunenGeom: GeoJSONFeatureCollection | undefined;
  kreiseGeom: GeoJSONFeatureCollection | undefined;
  regierungsbezirkeGeom: GeoJSONFeatureCollection | undefined;

  gemarkungen: any = [];

  selectedStates: any = {};
  selectedDistricts: any = {};
  selectedCounties: any = {};
  selectedKommunen: any = {};
  selectedGemarkungen: any = {};
  statesKN: string[] = [];
  districtsKN: string[] = [];
  countiesKN: string[] = [];
  kommunenKN: string[] = [];

  constructor(private http: HttpClient) {
    http.get('assets/json/geomdata/regions.json').subscribe(regions => this.regions = regions as {bundesland: string[]; bezirke: string[]; kreise: string[]; kommunen: string[]});
    http.get('assets/json/geomdata/bundeslandGeom.json').subscribe(bundeslandGeom => this.bundeslandGeom = bundeslandGeom as GeoJSONFeatureCollection);
    http.get('assets/json/geomdata/kommunenGeom.json').subscribe(kommunenGeom => this.kommunenGeom = kommunenGeom as GeoJSONFeatureCollection);
    http.get('assets/json/geomdata/kreiseGeom.json').subscribe(kreiseGeom => this.kreiseGeom = kreiseGeom as GeoJSONFeatureCollection);
    http.get('assets/json/geomdata/regierungsbezirkeGeom.json').subscribe(regierungsbezirkeGeom => this.regierungsbezirkeGeom = regierungsbezirkeGeom as GeoJSONFeatureCollection);
  }

  /**
   * Get fluren and gemarkungen
   * @param queryParam search term
   */
  getFlurGemarkungen(queryParam: string): Observable<any> {
    return this.http.get(searchFlurUrl, {
      params: {
        query: queryParam,
        limit: '50'
      }
    });
  }

  /**
   * Checks if the object is empty
   * @param regionObject any with selected regions
   */
  isEmpty(regionObject: any): boolean {
    // Check empty objects
    for (const key in regionObject) {
      if (regionObject.hasOwnProperty(key)) {
        return false;
      }
    }
    return true;
  }

  /**
   * Object contains an array with regions and their checked states (true or false)
   * An array with checked regions will be returned.
   * @param regionObject array with checked and unchecked regions
   */
  getTrueValues(regionObject: any): string[] {
    let trueValues: string[];
    trueValues = [];
    for (const prop in regionObject) {
      if (regionObject[prop]) {
        trueValues.push(prop);
      }
    }
    return trueValues;
  }

  /**
   * All selected regions will be collected
   */
  getSelectedRegions(): [] {
    let collectSelectedRegions: any;
    collectSelectedRegions = [];

    // Check if the object is empty
    if (!this.isEmpty(this.getSelectedStates())) {
      // Extract only true values
      const allSelectedStates = this.getTrueValues(this.getSelectedStates());
      if (allSelectedStates.length > 0) {
        // Two arrays will be merged.
        collectSelectedRegions = collectSelectedRegions.concat(allSelectedStates);
      }
    }

    if (!this.isEmpty(this.getSelectedDistricts())) {
      const allSelectedDistricts = this.getTrueValues(this.getSelectedDistricts());
      if (allSelectedDistricts.length > 0) {
        collectSelectedRegions = collectSelectedRegions.concat(allSelectedDistricts);
      }
    }

    if (!this.isEmpty(this.getSelectedCounties())) {
      const allSelectedCounties = this.getTrueValues(this.getSelectedCounties());
      if (allSelectedCounties.length > 0) {
        collectSelectedRegions = collectSelectedRegions.concat(allSelectedCounties);
      }
    }

    if (!this.isEmpty(this.getSelectedKommunen())) {
      const allSelectedKommunen = this.getTrueValues(this.getSelectedKommunen());
      if (allSelectedKommunen.length > 0) {
        collectSelectedRegions = collectSelectedRegions.concat(allSelectedKommunen);
      }
    }

    return collectSelectedRegions.sort();
  }

  resetStates(): void {
    this.setSelectedStates({});
    this.setSelectedDistricts({});
    this.setSelectedCounties({});
    this.setSelectedKommunen({});
    this.setSelectedGemarkungen({});
  }

  setSelectedStates(states: any): void {
    this.selectedStates = states;
  }

  setSelectedDistricts(districts: any): void {
    this.selectedDistricts = districts;
  }

  setSelectedCounties(counties: any): void {
    this.selectedCounties = counties;
  }

  setSelectedKommunen(kommunen: any): void {
    this.selectedKommunen = kommunen;
  }

  setSelectedGemarkungen(gemarkungen: any): void {
    this.selectedGemarkungen = gemarkungen;
  }

  setStatesKN(identifier: string): void {
    this.statesKN.push(identifier);
  }

  setDistrictsKN(identifier: string): void {
    this.districtsKN.push(identifier);
  }

  setCountiesKN(identifier: string): void {
    this.countiesKN.push(identifier);
  }

  setKommunenKN(identifier: string): void {
    this.kommunenKN.push(identifier);
  }

  getStatesKN(): string[] {
    return this.statesKN;
  }

  getDistrictsKN(): string[] {
    return this.districtsKN;
  }

  getCountiesKN(): string[] {
    return this.countiesKN;
  }

  getKommunenKN(): string[] {
    return this.kommunenKN;
  }

  clearAllKN(): void {
    this.statesKN = [];
    this.districtsKN = [];
    this.countiesKN = [];
    this.kommunenKN = [];
  }

  getSelectedStates(): any {
    return this.selectedStates;
  }

  getSelectedDistricts(): any {
    return this.selectedDistricts;
  }

  getSelectedCounties(): any {
    return this.selectedCounties;
  }

  getSelectedKommunen(): any {
    return this.selectedKommunen;
  }

  getState(): any {
    return this.regions.bundesland;
  }

  getDistrict(): any {
    return this.regions.bezirke;
  }

  getCounty(): any {
    return this.regions.kreise;
  }

  getKommunen(): any {
    return this.regions.kommunen;
  }

  getGeomDistrict(): GeoJSONFeatureCollection | undefined {
    return this.regierungsbezirkeGeom;
  }

  /**
   * The geometrie of a selected item like Arnsberg or Düsseldorf should be displayed on the map.
   * The function loops through every feature of the corresponding feature collection and retrieves
   * the geometry of the selected region.
   * @param tab current tab
   * @param selectedItem selected region
   */
  getGeomOfSelectedItem(tab: string, selectedItem: string): any {
    let geomArray: any;
    let geojson: any;
    let knArray: string[];
    let nameProperty = 'GN';
    let idProperty = 'KN';
    // Array of geometries depends on the active tab
    if (tab === titleDistrict) {
      geomArray = this.regierungsbezirkeGeom;
      knArray = this.districtsKN;
    } else if (tab === titleCounty) {
      geomArray = this.kreiseGeom;
      knArray = this.countiesKN;
    } else if (tab === titleKommunen) {
      geomArray = this.kommunenGeom;
      knArray = this.kommunenKN;
    } else if (tab === titleState) {
      geomArray = this.bundeslandGeom;
      knArray = this.statesKN;
      nameProperty = 'GEN';
      idProperty = 'LAN_kurz';
    }
    /*
     else if (tab === 'Gemarkungen / Fluren') {
      geomArray = gemarkungenGeom;
    }
    */
    // Loop through every feature of the geometries and find the selected region like Arnsberg or Bielefeld
    geomArray.features.map((item: any) => {
      if (item.properties[nameProperty] === selectedItem) {
        // Create new GeoJSON
        geojson =  new GeoJSON().readFeature(item);
        // Set feature id. Based on the id the feature can be better found
        geojson.setId(item.properties[idProperty]);
        knArray.push(item.properties[idProperty]);
      }
    });
    return geojson;
  }
}
